import { useTranslation } from '../../../../lib/i18n';

// 64px top gradient. Only the home/full pane needs it (the brand hamburger
// floats over the messages there). The floating chat has its own solid-row
// header, so the fade would just paint a white band under it on the cream
// field — kept mounted at opacity 0 there so it cross-fades with the 260ms
// slide instead of popping in when the viewport crosses the breakpoint.
export function TopFade({ visible }: { visible: boolean }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 64,
        zIndex: 3,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 260ms ease',
        background:
          'linear-gradient(180deg, rgba(var(--surface-base-rgb),0.85) 0%, rgba(var(--surface-base-rgb),0.55) 60%, transparent 100%)',
      }}
    />
  );
}

// Drag-drop overlay shown while files hover over the chat pane.
export function DragOverlay({ fileCount }: { fileCount: number }) {
  const { t } = useTranslation();
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 16,
        zIndex: 10,
        pointerEvents: 'none',
        border: '2px dashed rgba(var(--accent-rgb),0.55)',
        borderRadius: 'var(--sym-radius-container)',
        background: 'rgba(var(--surface-base-rgb),0.62)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        color: 'rgba(var(--ink-rgb),0.92)',
        letterSpacing: '0.04em',
      }}
    >
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 16V4M12 4l-4 4M12 4l4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
          stroke="rgba(var(--accent-rgb),0.85)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div style={{ fontSize: 15 }}>
        {fileCount > 1 ? t('chat.dropFiles', { count: fileCount }) : t('chat.dropFile')}
      </div>
    </div>
  );
}

// The spec's 28×28 round arrow-down control, horizontally centered and floating
// 28px above the composer. Its SCALE tracks the distance from the newest message
// (`progress` 0…1): it visibly EXPANDS from a point as the user scrolls away and SHRINKS
// back to nothing (scale 0 = invisible) as they return — the motion is the scale itself,
// not a fade — mirroring the native apps' FAB (docs/chat-arrow-down-behavior-handoff.md).
// Hit-testing starts only once it is materially visible (progress > 0.2), so an all-but-
// invisible button is never clickable. Rendered inside the form so it anchors to the
// composer's top edge whatever the composer's height is.
export function ScrollToLatestButton({ onClick, progress }: { onClick: () => void; progress: number }) {
  const { t } = useTranslation();
  const p = Math.min(Math.max(progress, 0), 1);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('chat.scrollToLatest')}
      title={t('chat.scrollToLatest')}
      aria-hidden={p <= 0.2}
      style={{
        position: 'absolute',
        top: -28,
        left: '50%',
        transform: `translate(-50%, -100%) scale(${p})`,
        pointerEvents: p > 0.2 ? 'auto' : 'none',
        // Back-out easing overshoots slightly for a springy pop, matching native's spring.
        transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        width: 28,
        height: 28,
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: 'var(--sym-bg-elevated)',
        color: 'rgba(var(--ink-rgb),0.8)',
        boxShadow: '0 2px 8px rgba(var(--ds-black-rgb), 0.14)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5,
        // No grey overlay flash when the FAB is tapped on a phone.
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
        <path
          d="M7 2.5v9M7 11.5l-4-4M7 11.5l4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

// Faint celestial horizon — a thin gradient line above the input instead of a
// hard 1px border. Looks like a sky-line at twilight.
export function HorizonLine() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        left: 32,
        right: 32,
        height: 1,
        background:
          'linear-gradient(90deg, transparent 0%, rgba(var(--accent-rgb),0.18) 30%, rgba(var(--accent-glow-rgb),0.18) 70%, transparent 100%)',
        pointerEvents: 'none',
      }}
    />
  );
}
